import React from 'react';

const SettingsTab = ({ settings, setSettings, saveSettings, loading }) => {
  const providers = [
    {
      id: 'openai', name: 'OpenAI', sub: 'GPT-4o, GPT-4o Mini, GPT-3.5',
      color: '#6366f1', icon: '⚡', models: [
        { value: 'gpt-4o', label: 'GPT-4o (Önerilen)' },
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Hızlı)' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Ekonomik)' }
      ]
    },
    {
      id: 'gemini', name: 'Google Gemini', sub: 'Gemini 2.0 Flash, 1.5 Pro',
      color: '#4285f4', icon: '🔮', models: [
        { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Önerilen)' },
        { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
        { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' }
      ]
    }
  ];

  const active = providers.find(p => p.id === settings.provider) || providers[0];

  return (
    <div className="st-tab">
      {/* AI Status */}
      <div className="st-card st-status-card">
        <div className="st-status-left">
          <div className={`st-status-indicator ${settings.is_enabled ? 'on' : 'off'}`} />
          <div>
            <div className="st-status-title">AI Asistan Durumu</div>
            <div className="st-status-sub">{settings.is_enabled ? 'AI aktif, mesajları işliyor' : 'AI devre dışı, manuel mod'}</div>
          </div>
        </div>
        <label className="st-switch">
          <input type="checkbox" checked={settings.is_enabled} onChange={e => setSettings({ ...settings, is_enabled: e.target.checked })} />
          <span className="st-slider" />
        </label>
      </div>

      {/* Provider Selection */}
      <div className="st-card">
        <h3 className="st-section-title">🧠 AI Sağlayıcı</h3>
        <p className="st-section-sub">Hangi AI modelini kullanacağınızı seçin</p>

        <div className="st-providers-grid">
          {providers.map(p => (
            <div
              key={p.id}
              className={`st-provider-card ${settings.provider === p.id ? 'selected' : ''}`}
              style={{ '--p-color': p.color }}
              onClick={() => setSettings({ ...settings, provider: p.id })}
            >
              {settings.provider === p.id && <div className="st-provider-check">✓</div>}
              <div className="st-provider-icon" style={{ background: p.color }}>{p.icon}</div>
              <div className="st-provider-name">{p.name}</div>
              <div className="st-provider-sub">{p.sub}</div>
            </div>
          ))}
        </div>

        {/* Provider Config */}
        <div className="st-config-section">
          {settings.provider === 'openai' && (
            <div className="st-config-grid">
              <div className="st-field">
                <label>OpenAI API Key</label>
                <input type="password" className="st-input" value={settings.openai_api_key || ''} onChange={e => setSettings({ ...settings, openai_api_key: e.target.value })} placeholder="sk-..." />
              </div>
              <div className="st-field">
                <label>Model</label>
                <select className="st-input" value={settings.model || 'gpt-4o'} onChange={e => setSettings({ ...settings, model: e.target.value })}>
                  {providers[0].models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
          )}
          {settings.provider === 'gemini' && (
            <div className="st-config-grid">
              <div className="st-field">
                <label>Google Gemini API Key</label>
                <input type="password" className="st-input" value={settings.gemini_api_key || ''} onChange={e => setSettings({ ...settings, gemini_api_key: e.target.value })} placeholder="AIza..." />
                <small className="st-hint">Google AI Studio'dan alabilirsiniz</small>
              </div>
              <div className="st-field">
                <label>Model</label>
                <select className="st-input" value={settings.gemini_model || 'gemini-2.0-flash'} onChange={e => setSettings({ ...settings, gemini_model: e.target.value })}>
                  {providers[1].models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="st-card">
        <h3 className="st-section-title">⚙️ Gelişmiş Ayarlar</h3>
        <p className="st-section-sub">Davranış ve performans parametrelerini ayarlayın</p>

        <div className="st-adv-grid">
          <div className="st-adv-item">
            <div className="st-adv-label">Güven Eşiği</div>
            <div className="st-adv-desc">AI'ın emin olmadan yanıtlamadığı eşik</div>
            <div className="st-range-wrap">
              <input
                type="range" min="0" max="100"
                value={settings.confidence_threshold || 85}
                onChange={e => setSettings({ ...settings, confidence_threshold: parseInt(e.target.value) })}
                className="st-range"
              />
              <span className="st-range-val">{settings.confidence_threshold || 85}%</span>
            </div>
          </div>

          <div className="st-adv-item">
            <div className="st-adv-label">Dil</div>
            <div className="st-adv-desc">Varsayılan yanıt dili</div>
            <select className="st-input st-adv-select" value={settings.language || 'tr'} onChange={e => setSettings({ ...settings, language: e.target.value })}>
              <option value="tr">🇹🇷 Türkçe</option>
              <option value="en">🇬🇧 İngilizce</option>
              <option value="de">🇩🇪 Almanca</option>
              <option value="fr">🇫🇷 Fransızca</option>
              <option value="ar">🇸🇦 Arapça</option>
            </select>
          </div>

          <div className="st-adv-item">
            <div className="st-adv-label">Max Konuşma Geçmişi</div>
            <div className="st-adv-desc">Bağlam için tutulan mesaj sayısı</div>
            <div className="st-range-wrap">
              <input
                type="range" min="1" max="50"
                value={settings.max_history || 20}
                onChange={e => setSettings({ ...settings, max_history: parseInt(e.target.value) })}
                className="st-range"
              />
              <span className="st-range-val">{settings.max_history || 20} mesaj</span>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Info */}
      <div className="st-card">
        <div className="st-plan-header">
          <div>
            <h3 className="st-section-title">📊 Plan Kullanımı</h3>
            <span className="st-plan-badge">Professional</span>
          </div>
        </div>
        <div className="st-usage-list">
          <div className="st-usage-item">
            <div className="st-usage-top">
              <span className="st-usage-label">🎙️ Voice Dakika</span>
              <span className="st-usage-num">840 / 2.000 dk</span>
            </div>
            <div className="st-usage-track"><div className="st-usage-fill" style={{ width: '42%', background:'linear-gradient(90deg,#7c3aed,#a78bfa)' }} /></div>
            <span className="st-usage-pct">42%</span>
          </div>
          <div className="st-usage-item">
            <div className="st-usage-top">
              <span className="st-usage-label">💬 Chat Mesajı</span>
              <span className="st-usage-num">9.750 / 15.000</span>
            </div>
            <div className="st-usage-track"><div className="st-usage-fill" style={{ width: '65%', background:'linear-gradient(90deg,#1d4ed8,#60A5FA)' }} /></div>
            <span className="st-usage-pct">65%</span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button className="st-save-btn" onClick={saveSettings} disabled={loading}>
          {loading ? '⏳ Kaydediliyor...' : '💾 Tüm Ayarları Kaydet'}
        </button>
      </div>

      <style>{`
        .st-tab { display:flex; flex-direction:column; gap:20px; }

        .st-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:18px; padding:24px; backdrop-filter:blur(16px); }

        /* Status Card */
        .st-status-card { display:flex; justify-content:space-between; align-items:center; background:linear-gradient(135deg,rgba(52,211,153,0.06),rgba(17,17,24,0.7)); border-color:rgba(52,211,153,0.15); }
        .st-status-left { display:flex; align-items:center; gap:16px; }
        .st-status-indicator { width:14px; height:14px; border-radius:50%; flex-shrink:0; }
        .st-status-indicator.on { background:#34D399; box-shadow:0 0 12px rgba(52,211,153,0.6); animation:pulse 2s infinite; }
        .st-status-indicator.off { background:#374151; }
        @keyframes pulse { 0%,100% { box-shadow:0 0 12px rgba(52,211,153,0.6); } 50% { box-shadow:0 0 20px rgba(52,211,153,0.9); } }
        .st-status-title { font-size:16px; font-weight:700; color:#F9FAFB; margin-bottom:3px; }
        .st-status-sub { font-size:13px; color:#6B7280; }

        /* Toggle Switch */
        .st-switch { position:relative; display:inline-block; width:52px; height:28px; flex-shrink:0; }
        .st-switch input { opacity:0; width:0; height:0; }
        .st-slider { position:absolute; cursor:pointer; inset:0; background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.1); border-radius:28px; transition:0.35s; }
        .st-slider:before { position:absolute; content:""; height:20px; width:20px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:0.35s; box-shadow:0 2px 6px rgba(0,0,0,0.3); }
        input:checked + .st-slider { background:rgba(52,211,153,0.2); border-color:rgba(52,211,153,0.5); }
        input:checked + .st-slider:before { transform:translateX(24px); background:#34D399; }

        .st-section-title { margin:0 0 4px; font-size:17px; font-weight:700; color:#F9FAFB; }
        .st-section-sub { margin:0 0 20px; font-size:13px; color:#6B7280; }

        /* Providers */
        .st-providers-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; margin-bottom:20px; }
        .st-provider-card { position:relative; padding:22px; border-radius:16px; border:2px solid rgba(255,255,255,0.07); background:rgba(255,255,255,0.03); cursor:pointer; transition:all 0.2s; display:flex; flex-direction:column; gap:6px; }
        .st-provider-card:hover { border-color:rgba(255,255,255,0.15); transform:translateY(-1px); }
        .st-provider-card.selected { border-color:var(--p-color); background:color-mix(in srgb, var(--p-color) 8%, transparent); }
        .st-provider-check { position:absolute; top:12px; right:12px; width:22px; height:22px; border-radius:50%; background:var(--p-color); display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; color:#fff; }
        .st-provider-icon { width:48px; height:48px; border-radius:13px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:4px; }
        .st-provider-name { font-size:17px; font-weight:800; color:#F9FAFB; }
        .st-provider-sub { font-size:12px; color:#6B7280; }

        .st-config-section { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:12px; padding:16px; }
        .st-config-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .st-field { display:flex; flex-direction:column; gap:6px; }
        .st-field label { font-size:11px; font-weight:700; color:#6B7280; text-transform:uppercase; letter-spacing:0.05em; }
        .st-input { padding:11px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; transition:border-color 0.2s; width:100%; box-sizing:border-box; }
        .st-input:focus { border-color:rgba(96,165,250,0.4); }
        .st-input option { background:#13131a; }
        .st-hint { font-size:11px; color:#4B5563; }

        /* Advanced */
        .st-adv-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(250px,1fr)); gap:16px; }
        .st-adv-item { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:12px; padding:16px; display:flex; flex-direction:column; gap:8px; }
        .st-adv-label { font-size:14px; font-weight:700; color:#E5E7EB; }
        .st-adv-desc { font-size:12px; color:#6B7280; }
        .st-range-wrap { display:flex; align-items:center; gap:12px; margin-top:4px; }
        .st-range { flex:1; -webkit-appearance:none; height:4px; border-radius:2px; background:rgba(255,255,255,0.1); outline:none; }
        .st-range::-webkit-slider-thumb { -webkit-appearance:none; width:16px; height:16px; border-radius:50%; background:#60A5FA; cursor:pointer; box-shadow:0 2px 6px rgba(96,165,250,0.4); }
        .st-range-val { font-size:13px; font-weight:700; color:#60A5FA; min-width:60px; text-align:right; }
        .st-adv-select { margin-top:4px; }

        /* Plan */
        .st-plan-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
        .st-plan-badge { padding:4px 12px; background:linear-gradient(135deg,rgba(var(--tertiary-rgb),0.2),rgba(167,139,250,0.2)); border:1px solid rgba(167,139,250,0.3); border-radius:20px; font-size:12px; font-weight:700; color:#a78bfa; }
        .st-usage-list { display:flex; flex-direction:column; gap:14px; }
        .st-usage-item { display:flex; flex-direction:column; gap:6px; }
        .st-usage-top { display:flex; justify-content:space-between; align-items:center; }
        .st-usage-label { font-size:13px; font-weight:600; color:#E5E7EB; }
        .st-usage-num { font-size:12px; color:#6B7280; }
        .st-usage-track { height:6px; background:rgba(255,255,255,0.07); border-radius:3px; overflow:hidden; }
        .st-usage-fill { height:100%; border-radius:3px; transition:width 0.5s; }
        .st-usage-pct { font-size:11px; color:#6B7280; text-align:right; }

        .st-save-btn { padding:12px 28px; background:linear-gradient(135deg,#7c3aed,#a78bfa); border:none; border-radius:12px; color:#fff; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.2s; }
        .st-save-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 24px rgba(124,58,237,0.4); }
        .st-save-btn:disabled { opacity:0.6; cursor:not-allowed; }

        @media (max-width:700px) {
          .st-providers-grid { grid-template-columns:1fr; }
          .st-config-grid { grid-template-columns:1fr; }
        }
      `}</style>
    </div>
  );
};

export default SettingsTab;
